import { Box, Stack, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { motion, LayoutGroup } from 'framer-motion'
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined'
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'

export type ViewMode = 'kanban' | 'table' | 'calendar' | 'workload'

interface Option {
  value: ViewMode
  label: string
  icon: React.ElementType
  isNew?: boolean
}

const OPTIONS: Option[] = [
  { value: 'kanban',   label: 'Kanban',   icon: ViewKanbanOutlinedIcon },
  { value: 'table',    label: 'Table',    icon: TableRowsOutlinedIcon },
  { value: 'calendar', label: 'Calendar', icon: CalendarMonthOutlinedIcon },
  { value: 'workload', label: 'Workload', icon: TimelineOutlinedIcon, isNew: true },
]

export function ViewSwitcher({
  value, onChange,
}: {
  value: ViewMode
  onChange: (v: ViewMode) => void
}) {
  const theme = useTheme()
  const pm = theme.palette.primary.main
  const pillBg = alpha(pm, theme.palette.mode === 'light' ? 0.10 : 0.22)
  const pillShadow = theme.palette.mode === 'light'
    ? `0 1px 3px ${alpha(pm, 0.22)}, inset 0 0 0 1px ${alpha(pm, 0.18)}`
    : `0 1px 3px rgba(0,0,0,0.32), inset 0 0 0 1px ${alpha(pm, 0.30)}`

  return (
    <LayoutGroup id="wo-view-switcher">
      <Box
        sx={{
          display: 'inline-flex',
          p: 0.5,
          gap: 0.25,
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative',
        }}
      >
        {OPTIONS.map((opt) => {
          const Icon = opt.icon
          const active = value === opt.value
          return (
            <Box
              key={opt.value}
              component="button"
              onClick={() => onChange(opt.value)}
              sx={{
                position: 'relative',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                px: 2, py: 0.85,
                fontFamily: 'inherit',
                color: active ? 'primary.main' : 'text.secondary',
                transition: 'color 200ms ease',
                borderRadius: 1,
                '&:hover': {
                  color: active ? 'primary.main' : 'text.primary',
                },
              }}
            >
              {/* Sliding pill indicator — shared layoutId animates between tabs */}
              {active && (
                <motion.div
                  layoutId="wo-view-pill"
                  transition={{ type: 'spring', stiffness: 520, damping: 38, mass: 0.8 }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 6,
                    background: pillBg,
                    boxShadow: pillShadow,
                    zIndex: 0,
                  }}
                />
              )}
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.85}
                sx={{ position: 'relative', zIndex: 1 }}
              >
                <Icon sx={{ fontSize: 16 }} />
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    letterSpacing: '-0.005em',
                    color: 'inherit',
                  }}
                >
                  {opt.label}
                </Typography>
                {opt.isNew && (
                  <Box
                    component="span"
                    sx={{
                      px: 0.65, py: 0.05,
                      borderRadius: 99,
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      lineHeight: 1.4,
                      color: '#fff',
                      bgcolor: 'primary.main',
                      textTransform: 'uppercase',
                    }}
                  >
                    New
                  </Box>
                )}
              </Stack>
            </Box>
          )
        })}
      </Box>
    </LayoutGroup>
  )
}
